import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
} from '@mui/material';
import EuroIcon from '@mui/icons-material/Euro';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import axios from 'axios';
import { C } from '../theme';
import { countries, getCitiesByCountry, City } from '../data/locations';
import { useAuth } from '../contexts/AuthContext';
import LocationPicker from '../components/LocationPicker';

const landTypes = [
  'Poljoprivredno zemljište',
  'Gradsko zemljište',
  'Šumsko zemljište',
  'Zemljište za izgradnju',
  'Zemljište za rekreaciju',
];

const landTypeMap: Record<string, string> = {
  'Poljoprivredno zemljište': 'agricultural',
  'Gradsko zemljište': 'residential',
  'Šumsko zemljište': 'commercial',
  'Zemljište za izgradnju': 'industrial',
  'Zemljište za rekreaciju': 'recreational',
};

const CreateAd: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [countryId, setCountryId] = useState('');
  const [cityId, setCityId] = useState('');
  const [landType, setLandType] = useState('');
  const [area, setArea] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    // Redirect non-authenticated users to login
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (countryId) {
      setAvailableCities(getCitiesByCountry(Number(countryId)));
      setCityId('');
    } else {
      setAvailableCities([]);
    }
  }, [countryId]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      if (images.length + newFiles.length > 10) {
        setError('Možete dodati maksimalno 10 slika');
        return;
      }

      setImages(prev => [...prev, ...newFiles]);
      
      // Create preview URLs
      const newPreviewUrls = newFiles.map(file => URL.createObjectURL(file));
      setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => {
      const newUrls = [...prev];
      URL.revokeObjectURL(newUrls[index]);
      return newUrls.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    // Validate numeric IDs for country and city
    if (isNaN(Number(countryId)) || isNaN(Number(cityId))) {
      setError('Molimo izaberite državu i grad iz liste. (ID mora biti broj)');
      setLoading(false);
      return;
    }
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('price', String(Number(price)));
      formData.append('country_id', String(Number(countryId)));
      formData.append('city_id', String(Number(cityId)));
      formData.append('land_type', landTypeMap[landType] || landType);
      formData.append('area', String(Number(area)));
      if (location) {
        formData.append('latitude', String(location.lat));
        formData.append('longitude', String(location.lng));
      }
      images.forEach(image => {
        formData.append('images', image);
      });
      // Debug: log all form values
      console.log('CreateAd form values:', {
        title, description, price: Number(price), countryId: Number(countryId), cityId: Number(cityId), landType: landTypeMap[landType] || landType, area: Number(area), images
      });
      // Debug: log FormData entries
      Array.from(formData.entries()).forEach(pair => {
        console.log(pair[0] + ':', pair[1]);
      });
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/ads',
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`
            // Do NOT set Content-Type for FormData! Let Axios/browser handle it.
          },
        }
      );
      navigate('/my-ads');
    } catch (err) {
      setError('Greška pri kreiranju oglasa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ pt: '110px', pb: 8, backgroundColor: C.bg, minHeight: '100vh' }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 600,
              mb: 2,
              color: C.white,
            }}
          >
            Postavi oglas
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: C.muted,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Kreirajte privlačan oglas za vaše zemljište i povežite se sa pravim investitorima
          </Typography>
        </Box>

        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, md: 5 },
            backgroundColor: C.surface,
            borderRadius: 4,
            border: `1px solid ${C.line}`,
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                  Osnovne informacije
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Naslov oglasa"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Npr. Atraktivno zemljište u Beogradu"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Cena (€)"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EuroIcon sx={{ color: C.muted }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Površina (m²)"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  type="number"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                  Lokacija
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Država</InputLabel>
                  <Select
                    value={countryId}
                    label="Država"
                    onChange={(e) => setCountryId(e.target.value)}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id} value={country.id}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Grad</InputLabel>
                  <Select
                    value={cityId}
                    label="Grad"
                    onChange={(e) => setCityId(e.target.value)}
                    disabled={!countryId}
                  >
                    {availableCities.map((city) => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Tip zemljišta</InputLabel>
                  <Select
                    value={landType}
                    label="Tip zemljišta"
                    onChange={(e) => setLandType(e.target.value)}
                  >
                    {landTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                  Opis
                </Typography>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  label="Detaljan opis"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Detaljno opišite zemljište, njegove karakteristike, prednosti i potencijalne mogućnosti..."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: C.white }}>
                  Lokacija na mapi
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: C.muted }}>
                  Opciono. Lokacija je vidljiva samo vama i investitorima.
                </Typography>
                <LocationPicker value={location} onChange={setLocation} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                  Slike
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    type="file"
                    multiple
                    onChange={handleImageChange}
                  />
                  <label htmlFor="image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<AddPhotoAlternateIcon />}
                      sx={{
                        borderColor: 'rgba(243,192,21,0.55)',
                        color: C.white,
                        '&:hover': {
                          background: C.goldGrad,
                          color: C.onGold,
                        },
                      }}
                    >
                      Dodaj slike
                    </Button>
                  </label>
                  <Typography variant="body2" sx={{ mt: 1, color: C.muted }}>
                    Možete dodati do 10 slika (maksimalna veličina: 5MB po slici)
                  </Typography>
                </Box>

                {previewUrls.length > 0 && (
                  <ImageList sx={{ width: '100%', height: 450 }} cols={3} rowHeight={164}>
                    {previewUrls.map((url, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          loading="lazy"
                          style={{ height: '100%', objectFit: 'cover' }}
                        />
                        <ImageListItemBar
                          position="top"
                          actionIcon={
                            <IconButton
                              sx={{ color: 'white' }}
                              onClick={() => removeImage(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                )}
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', pt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/my-ads')}
                    sx={{
                      borderColor: 'rgba(243,192,21,0.55)',
                      color: C.white,
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        background: C.goldGrad,
                        color: C.onGold,
                      },
                    }}
                  >
                    Otkaži
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                      background: C.goldGrad,
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        background: C.goldGrad,
                      },
                      '&:disabled': {
                        backgroundColor: C.surface2,
                      },
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Postavi oglas'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CreateAd; 